import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { ECONOMICS } from "@/data/marketing/homepage";
import type { EconomicsColumn } from "@/types/homepage";

const Column = ({ column }: { column: EconomicsColumn }) => {
  const isGenie = column.kind === "genie";

  const inner = (
    <div
      className={cn(
        "flex h-full flex-col rounded-[18px] p-7 md:p-8",
        isGenie ? "bg-white" : "border border-[#E5EAF0] bg-white"
      )}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#222B45]">{column.heading}</h3>
        {isGenie && (
          <span className="rounded-full bg-genie-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-genie-primary">
            Recommended
          </span>
        )}
      </div>

      <div className="mt-4 text-5xl font-bold leading-none md:text-6xl">
        {isGenie ? (
          <GradientText>{column.price}</GradientText>
        ) : (
          <span className="text-gray-400">{column.price}</span>
        )}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-gray-600">
        {column.priceNote}
      </p>

      <ul className="mt-6 space-y-3 border-t border-[#E5EAF0] pt-6">
        {column.points.map((point) => (
          <li key={point.text} className="flex items-start gap-3">
            <span
              className={cn(
                "mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full",
                isGenie ? "bg-genie-primary/10" : "bg-[#F3F4F6]"
              )}
            >
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  isGenie
                    ? "bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
                    : "bg-gray-400"
                )}
              />
            </span>
            <span className="text-sm leading-relaxed text-gray-700">
              {point.text}
            </span>
          </li>
        ))}
      </ul>

      <p
        className={cn(
          "mt-6 text-sm font-semibold",
          isGenie ? "text-genie-primary" : "text-gray-500"
        )}
      >
        {column.scalesBy}
      </p>
    </div>
  );

  // Genie column gets the signature gradient border.
  return isGenie ? (
    <div className="p-[2px] rounded-[20px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-xl">
      {inner}
    </div>
  ) : (
    <div className="rounded-[20px]">{inner}</div>
  );
};

export default function HomeEconomics() {
  return (
    <section className="w-full bg-[#F9FAFC] py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="The same questions"
          title={
            <>
              The same questions.
              <br />
              <GradientText>Two very different days</GradientText>
            </>
          }
          subtitle="Every business answers the same questions all day. The only difference is whether they get answered the moment your customer asks, or whenever someone's free."
        />

        <div className="mt-12 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
          {ECONOMICS.map((column) => (
            <Column key={column.id} column={column} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:opacity-95"
            style={{
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          >
            See pricing
            <ChevronRight className="ml-1 h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
